/**
 * Shared Slack helpers used across apps (backend + integrations).
 *
 * Consolidated from the previous per-app implementations. Uses the global
 * `fetch` (Node >=24) so no HTTP client dependency is required.
 */

/**
 * Ayor's Slack DMs — the default destination for alert/slackbot messages.
 */
export const AYORS_DMS = "UE04CRG9F";

/** Default channel read by `getMessagesInSlackChannel` (#operations_app). */
const OPERATIONS_APP_CHANNEL = "C02G33T34UQ";

/**
 * `#dev_testing_app` channel ID. Slack's `conversations.history` (used to
 * find/reuse today's daily-thread header) requires the stable channel ID
 * rather than the `#name`, so consumers should reference this constant.
 */
export const DEV_TESTING_APP_CHANNEL = "C070J3Z5NH0";

/**
 * `#operations_metabase` channel ID. Like {@link DEV_TESTING_APP_CHANNEL},
 * daily-thread lookups require the stable channel ID rather than the `#name`.
 */
export const OPERATIONS_METABASE_CHANNEL = "C0381JCJKL4";

/**
 * Serialize an object into `key: value` lines, e.g. `{ loaded: 5 }` becomes
 * `"loaded: 5\n"`. Handy for dumping context into a Slack message body.
 */
export function objectToString(object: Record<string, unknown>): string {
  let str = "";
  for (const [key, value] of Object.entries(object)) {
    str += `${key}: ${value}\n`; // convert {"loaded": 5} to loaded: 5
  }
  return str;
}

// Minimal single-slot throttle replacing the previous `p-throttle`
// configuration (limit: 1, interval: 1000ms). Throttled calls are chained so
// each one starts at least `THROTTLE_INTERVAL_MS` after the previous settled,
// which keeps us from spamming the Slack API.
const THROTTLE_INTERVAL_MS = 1000;
let throttleChain: Promise<unknown> = Promise.resolve();

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function runThrottled<T>(fn: () => Promise<T>): Promise<T> {
  const result = throttleChain.then(fn);
  // Advance the chain regardless of success/failure so one rejected message
  // doesn't wedge the queue.
  throttleChain = result.then(
    () => delay(THROTTLE_INTERVAL_MS),
    () => delay(THROTTLE_INTERVAL_MS)
  );
  return result;
}

/** Minimal logger interface accepted by {@link sendSlackMessage}. */
export interface SlackLogger {
  info: (message: string) => void;
}

/**
 * Whether Slack calls should actually hit the network. Only `true` in
 * `production` or when `SEND_SLACK_MESSAGES === "true"`, so test/local
 * environments never make real Slack requests with a missing/dev token.
 */
function slackSendingEnabled(): boolean {
  return (
    process.env.NODE_ENV === "production" ||
    process.env.SEND_SLACK_MESSAGES === "true"
  );
}

/**
 * Post a message to Slack.
 *
 * Only actually hits the Slack API when `NODE_ENV === "production"` or
 * `SEND_SLACK_MESSAGES === "true"`; in `local` it logs via `logger`. Requires
 * `SLACK_API_BOT_ACCESS_TOKEN` in the environment when sending.
 *
 * @param text    Message body.
 * @param channel Channel or user ID. Defaults to {@link AYORS_DMS}.
 * @param thread  Parent message `ts` to reply in-thread. Optional.
 * @param throttle When true, serialize sends to one per second.
 * @param logger  Logger used for the `local` debug line. Defaults to `console`.
 *                Pass e.g. `fastify.log` to route through an app logger.
 */
export async function sendSlackMessage(
  text: string,
  channel: string = AYORS_DMS,
  thread: string | null = null,
  throttle = false,
  logger: SlackLogger = console
): Promise<unknown> {
  const messageFn = async (): Promise<unknown> => {
    if (process.env.NODE_ENV === "local") {
      logger.info(`${text}, ${channel}, ${thread}`);
    }

    if (slackSendingEnabled()) {
      const body: Record<string, unknown> = { text, channel };

      if (thread) {
        body.thread_ts = thread;
      }

      const response = await fetch("https://slack.com/api/chat.postMessage", {
        method: "POST",
        headers: {
          "content-type": "application/json; charset=UTF-8",
          Authorization: `Bearer ${process.env.SLACK_API_BOT_ACCESS_TOKEN}`,
        },
        body: JSON.stringify(body),
      });

      // fetch resolves on non-2xx, so surface HTTP failures explicitly
      // instead of silently returning an error body as if it succeeded.
      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(
          `Slack chat.postMessage failed: ${response.status} ${response.statusText} ${errorBody}`
        );
      }

      // Slack returns HTTP 200 even for logical failures (e.g. invalid_auth,
      // channel_not_found), signalling them via `ok: false` in the body.
      const data = (await response.json()) as { ok?: boolean; error?: string };
      if (!data.ok) {
        throw new Error(
          `Slack chat.postMessage failed: ${data.error ?? "unknown_error"}`
        );
      }

      return data;
    }

    return undefined;
  };

  if (!throttle) return messageFn();
  return runThrottled(messageFn);
}

export interface GetMessagesInSlackChannelOptions {
  channel?: string;
  limit?: number;
  [key: string]: string | number | undefined;
}

/**
 * Read recent messages from a Slack channel via `conversations.history`.
 * Requires `SLACK_API_USER_ACCESS_TOKEN` in the environment.
 *
 * Gated by {@link slackSendingEnabled}: in test/local environments this makes
 * no network call and resolves to `undefined`, matching `sendSlackMessage`'s
 * behavior so daily-thread lookups don't hit Slack with a missing/dev token.
 */
export async function getMessagesInSlackChannel({
  channel = OPERATIONS_APP_CHANNEL,
  limit = 200,
  ...rest
}: GetMessagesInSlackChannelOptions = {}): Promise<unknown> {
  if (!slackSendingEnabled()) {
    return undefined;
  }

  const queryParams = new URLSearchParams({
    channel,
    limit: String(limit),
    ...Object.fromEntries(
      Object.entries(rest)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => [key, String(value)])
    ),
  });

  const response = await fetch(
    `https://slack.com/api/conversations.history?${queryParams.toString()}`,
    {
      method: "GET",
      headers: {
        "content-type": "application/json; charset=UTF-8",
        Authorization: `Bearer ${process.env.SLACK_API_USER_ACCESS_TOKEN}`,
      },
    }
  );

  // fetch resolves on non-2xx, so surface HTTP failures explicitly
  // instead of silently returning an error body as if it succeeded.
  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(
      `Slack conversations.history failed: ${response.status} ${response.statusText} ${errorBody}`
    );
  }

  // Slack returns HTTP 200 even for logical failures (e.g. invalid_auth,
  // channel_not_found), signalling them via `ok: false` in the body.
  const data = (await response.json()) as { ok?: boolean; error?: string };
  if (!data.ok) {
    throw new Error(
      `Slack conversations.history failed: ${data.error ?? "unknown_error"}`
    );
  }

  return data;
}

interface CachedThread {
  ts: string;
  createdAt: number;
}

// In-memory cache of today's thread ts per (channel, title), so repeated
// calls within the same process don't all hit conversations.history.
const dailyThreadCache: Record<string, CachedThread> = {};

// In-flight lookup-or-create promises per (channel, title). Concurrent callers
// share the same promise so only ONE of them hits conversations.history /
// creates the header, instead of each racing to post its own top-level
// message. Cleared on rejection so a failed attempt doesn't poison the key.
const dailyThreadInFlight: Record<string, Promise<CachedThread | undefined>> =
  {};

export interface SendDailyThreadedSlackMessageOptions {
  title: string;
  message: string;
  channel: string;
}

/**
 * Resolve the header thread `ts` for `(channel, title)` today, creating the
 * header if it doesn't exist yet. Dedupes concurrent callers behind a single
 * in-flight promise so bursts (e.g. many shape-validation failures at once)
 * don't each create their own top-level header message.
 */
function resolveDailyThread(
  channel: string,
  title: string,
  cacheKey: string
): Promise<CachedThread | undefined> {
  const cached = dailyThreadCache[cacheKey];
  const cacheValid =
    cached && cached.createdAt >= Date.now() - 24 * 60 * 60 * 1000;
  if (cacheValid) {
    return Promise.resolve(cached);
  }

  const existingInFlight = dailyThreadInFlight[cacheKey];
  if (existingInFlight) {
    return existingInFlight;
  }

  const lookupOrCreate = (async (): Promise<CachedThread | undefined> => {
    const history = (await getMessagesInSlackChannel({
      channel,
      // A wider-than-24h lookback avoids missing today's header near
      // midnight/across timezones.
      oldest: Date.now() / 1000 - 48 * 60 * 60,
    })) as { messages?: { ts: string; text?: string }[] } | undefined;

    const foundMessages = Array.isArray(history?.messages)
      ? history.messages
      : [];
    const existing = foundMessages.find(
      m => m && m.text && m.text.toLowerCase() === title.toLowerCase()
    );

    let resolved: CachedThread | undefined;
    if (existing) {
      resolved = { ts: existing.ts, createdAt: Date.now() };
    } else {
      const resp = (await sendSlackMessage(title, channel, undefined, true)) as
        | { ts?: string }
        | undefined;
      resolved = resp?.ts ? { ts: resp.ts, createdAt: Date.now() } : undefined;
    }

    if (resolved) {
      dailyThreadCache[cacheKey] = resolved;
    }
    return resolved;
  })();

  dailyThreadInFlight[cacheKey] = lookupOrCreate;

  // Clear the in-flight slot once settled. On success the result is now in
  // dailyThreadCache; on failure we drop it so the next caller can retry.
  return lookupOrCreate.finally(() => {
    if (dailyThreadInFlight[cacheKey] === lookupOrCreate) {
      delete dailyThreadInFlight[cacheKey];
    }
  });
}

/**
 * Posts `message` as a threaded reply under a daily header message titled
 * `title`, creating the header if it doesn't exist yet today. This keeps
 * repeated failures (e.g. many shape-validation errors in one test run)
 * collapsed into a single thread instead of spamming the channel with one
 * top-level message per failure.
 *
 * Best-effort: if the thread lookup/creation fails for any reason, falls
 * back to sending `message` as a plain, un-threaded message so the alert
 * still goes out.
 */
export async function sendDailyThreadedSlackMessage({
  title,
  message,
  channel,
}: SendDailyThreadedSlackMessageOptions): Promise<unknown> {
  const cacheKey = `${channel}:${title}`;

  try {
    const cached = await resolveDailyThread(channel, title, cacheKey);
    return sendSlackMessage(message, channel, cached?.ts, true);
  } catch {
    // Thread lookup/creation failed - fall back to a plain message rather
    // than dropping the alert entirely.
    return sendSlackMessage(message, channel, undefined, true);
  }
}
